import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Link, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { useFormik } from "formik";

import { resetPasswordValidation } from "../../../services/validations/publicPages/resetPassword.validation";
import { LoginFormStyles } from "../login/LoginFormStyles";
import { BenefitLogo } from "../../../assets/Icons";
import Button from "../../button";
import * as authService from "../../../services/api/auth.service";
import Unmasked from "../../../assets/images/002-visibility.png";
import Masked from "../../../assets/images/003-visibility-1.png";
import { useTranslation } from "react-i18next";

export const ResetPassword = ({ email, hash, expiresAt }) => {
  const { t } = useTranslation();
  const [maskedNewPassword, setMaskedNewPassword] = useState(true);
  const [maskedNewPasswordConfirm, setMaskedNewPasswordConfirm] = useState(true);
  const history = useHistory();

  const expires = Number(expiresAt);

  const isExpired = useMemo(() => {
    if (!expires || Number.isNaN(expires)) return true;
    return Date.now() > expires;
  }, [expires]);

  const handleResetPassword = async (values, { resetForm }) => {
    if (isExpired) {
      return toast.error(t("reset_link_expired"));
    }

    const response = await authService.resetPassword(values);

    if (response.hasError) {
      return toast.error(
        response.errorMessage
          ? t(response.errorMessage)
          : t("failed_to_reset_password")
      );
    }

    resetForm();
    toast.success(t(response.message));
    history.push("/");
  };

  const formik = useFormik({
    initialValues: {
      email,
      password: "",
      passwordConfirmation: "",
      hash
    },
    validate: resetPasswordValidation,
    onSubmit: handleResetPassword
  });

  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleChange,
    handleBlur
  } = formik;

  return (
    <LoginFormStyles>
      <div className="authWindow">
        {isExpired && (
          <div className="expiredOverlay">
            <div className="expiredMessage">
              {t("reset_link_expired")}
            </div>
          </div>
        )}

        <BenefitLogo color="#1568bf" />

        <div className="formContainer">
          <form
            onSubmit={handleSubmit}
            className={`form ${isExpired ? "disabledForm" : ""}`}
            autoComplete="off"
          >
            <label className="label" htmlFor="email">
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                Email:
                <div className="errorMsg">
                  {errors.email && touched.email ? errors.email : ""}
                </div>
              </div>
              <input
                className="inputs"
                id="email"
                name="email"
                type="email"
                value={values.email}
                disabled
              />
            </label>

            <label className="label" htmlFor="password">
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                {t("new_password")}:
                <div className="errorMsg">
                  {errors.password && touched.password ? errors.password : ""}
                </div>
              </div>
              <div style={{ position: "relative" }}>
                <input
                  className="inputs"
                  id="password"
                  name="password"
                  type={maskedNewPassword ? "password" : "text"}
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <img
                  src={maskedNewPassword ? Masked : Unmasked}
                  onClick={() => setMaskedNewPassword(!maskedNewPassword)}
                  alt="masked"
                  style={{ position: "absolute", right: -30, cursor: "pointer" }}
                />
              </div>
            </label>

            <label className="label" htmlFor="passwordConfirmation">
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                {t("confirm_password")}:
                <div className="errorMsg">
                  {errors.passwordConfirmation && touched.passwordConfirmation
                    ? errors.passwordConfirmation
                    : ""}
                </div>
              </div>
              <div style={{ position: "relative" }}>
                <input
                  className="inputs"
                  id="passwordConfirmation"
                  name="passwordConfirmation"
                  type={maskedNewPasswordConfirm ? "password" : "text"}
                  value={values.passwordConfirmation}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <img
                  src={maskedNewPasswordConfirm ? Masked : Unmasked}
                  onClick={() =>
                    setMaskedNewPasswordConfirm(!maskedNewPasswordConfirm)
                  }
                  alt="masked"
                  style={{ position: "absolute", right: -30, cursor: "pointer" }}
                />
              </div>
            </label>

            <div className="buttonContainer">
              <Link to="/" className="forgotPassword">
                {t("Login")}
              </Link>

              <Button type="submit" disabled={isExpired}>
                {t("reset_password")}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <div className="bannerWindow" />
    </LoginFormStyles>
  );
};

ResetPassword.propTypes = {
  email: PropTypes.string,
  hash: PropTypes.string,
  expiresAt: PropTypes.string
};

export default ResetPassword;
